import clsx from "clsx";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { Button } from "ui-kit/Button";
import { useWindowSize } from "usehooks-ts";
import { useTranslation } from "react-i18next";
import { useUserStore } from "store/user";
import {
  gradientRowBgClass,
  referralCtaButtonClass,
  shadowAndBorderClass,
} from "screens/Dashboard/utils/classes";
import { AnimatedGradientBorder, GlowCard } from "screens/Landing/components/shared";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { Copy, Wallet } from "lucide-react";

interface ReferralRewardsProps {
  referralLink: string;
}

export function ReferralRewards({ referralLink }: ReferralRewardsProps) {
  const [copiedText, copyToClipboard] = useCopyToClipboard(2000);
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { openConnectModal } = useConnectModal();

  const { width } = useWindowSize();
  const isMobile = width < 1024;

  const referralText =
    isMobile && referralLink?.length > 16
      ? referralLink.slice(0, 16) + "..."
      : referralLink;

  const handleCopy = () => {
    copyToClipboard(referralLink);
  };

  return (
    <AnimatedGradientBorder className="rounded-[28px] max-lg:rounded-[24px] w-full">
    <GlowCard className="flex flex-col w-full rounded-[28px] max-lg:rounded-[24px] max-lg:before:!rounded-[24px] before:!rounded-[28px] bg-[#111111] p-[12px] max-lg:p-[12px]">
      <div
        className={clsx(
          "flex max-lg:mb-[16px] items-center rounded-[16px] before:!rounded-[16px] max-lg:rounded-[12px] max-lg:before:!rounded-[12px] max-lg:py-[8px] max-lg:px-[13px] lg:p-[8px] lg:pl-[16px]",
          gradientRowBgClass
        )}
      >
        <h2 className="text-[24px] max-lg:text-20m font-luxora-medium leading-[110%] text-white">
          {t("Invite your friends to Earn Rewards")}
        </h2>
      </div>

      <div
        className={clsx(
          "flex max-lg:mb-[16px] flex-col max-lg:p-[14px] gap-[4px] max-lg:gap-[5px] max-lg:rounded-[12px] rounded-[16px] border-[2px] border-white/[0.06] p-[16px] lg:mt-[12px]"
        )}
      >
        <h3 className="text-[18px] max-lg:text-18m font-luxora-medium leading-[1.2] text-white">
          {t("Thank you for Promoting ATTS!")}
        </h3>
        <p className="text-[14px] max-lg:text-14m font-luxora-regular leading-[1.2] text-white/40">
          {t("Earn up to 10% $ATTS purchased using your referral link")}
        </p>
      </div>

      <div
        className={clsx(
          "flex items-center max-lg:justify-between rounded-[16px] max-lg:rounded-[12px] bg-[#161616] font-luxora-regular max-lg:before:!rounded-[12px] before:!rounded-[16px] max-lg:pr-[5px] max-lg:py-[5px] max-lg:pl-[16px] pr-[4px] pl-[24px] py-[8px] lg:mt-[12px]",
          shadowAndBorderClass
        )}
      >
        <p className="flex-grow w-0 text-left max-lg:text-16m text-[20px] leading-[110%] text-white/40 font-luxora-regular max-lg:line-clamp-1 lg:break-words">
          {user && user.referralCode?.code
            ? referralText
            : "Connect Wallet to get the link"}
        </p>

        <div className="relative">
          {user.referralCode?.code ? (
            <Button
              className={referralCtaButtonClass}
              onClick={handleCopy}
              disabled={!!copiedText || !user.referralCode?.code}
            >
              <span className="font-luxora-medium font-normal text-black text-[16px] max-lg:text-[16px] tracking-[0] leading-[110%] whitespace-nowrap">
                {copiedText ? t("Copied!") : t("Copy link")}
              </span>
              <Copy
                className={clsx(
                  "w-[18px] h-[18px] max-lg:w-[18px] max-lg:h-[18px] shrink-0",
                  !user.referralCode?.code ? "text-gray-500" : "text-black"
                )}
                aria-hidden
              />
            </Button>
          ) : (
            <Button
              onClick={() => { if (openConnectModal) openConnectModal(); }}
              className={referralCtaButtonClass}
            >
              <span className="font-luxora-medium font-normal text-black text-[16px] max-lg:text-[16px] tracking-[0] leading-[110%] whitespace-nowrap">
                {t("header.connectWalletforAllocation")}
              </span>
              <Wallet className="w-[18px] h-[18px] max-lg:w-[18px] max-lg:h-[18px] text-black shrink-0" />
            </Button>
          )}
        </div>
      </div>
    </GlowCard>
    </AnimatedGradientBorder>
  );
}
